import {
  Form,
  Select,
  Button,
  Input,
  Card,
  Col,
  Row,
  Pagination,
  PaginationProps,
  Skeleton,
  Tooltip,
} from 'antd'
import React, { FC, useState, useRef, useEffect } from 'react'

import cls from 'classnames'
import { ReactComponent as AddWalletIcon } from '@/assets/icons/addWallet.svg'
import { ReactComponent as CloseEyeIcon } from '@/assets/icons/closeEye.svg'
import { ReactComponent as OpenEyeIcon } from '@/assets/icons/openEye.svg'
import { ReactComponent as CopyIcon } from '@/assets/icons/copy.svg'
import styles from './index.module.scss'
import { useDispatch } from 'react-redux'
import {
  resetWalletsQuery,
  setWalletsQuery,
  setWalletsSearchValue,
  resetShowWallets,
} from '@/store/wallet'
import { AppDispatch } from '@/store/types'
import { WalletPaginationModel, WalletType, WalletItemModel } from '@/services/apis/wallet/models'
import { CURRENCY, DEFAULT_WALLET_LIST_PAGE_SIZE, WALLET_TYPE } from '@/constants'
import CreateWalletDrawer from '../CreateWalletDrawer'
import Empty from '@/components/Empty'
import { NavigateFunction, useLocation, useNavigate } from 'react-router-dom'
import walletApi from '@/services/apis/wallet'
import InfiniteScroll from 'react-infinite-scroll-component'
import WalletButton from '@/components/WalletButton'
import { useTranslation } from '@/services/i18n'
import { maskAddress } from '@/utils/address'
import { useCheckBackupStatus } from '@/hooks/user'
import BackupModal from '@/components/BackupModal'
import { useSelectWorkspaceUserInfo } from '@/hooks/user'

import { useCanManageWallet, useIsManagement } from '@/hooks/permission'
import {
  useGetIsSendAddressRecoveryFile,
  useGetWalletList,
  useSelectWalletsSearchValue,
} from '@/hooks/wallet'
import { copyTextToClipboard } from '@/utils/clipboard'
import { formatUSD } from '@/utils/format'
import { useHover } from 'ahooks'
import HideWallet from '@/assets/images/hideWallet.png'
import HideWallet2 from '@/assets/images/hideWallet2.png'
import ShowWallet from '@/assets/images/showWallet.png'

import ShadowView from '@/components/ShadowView'
import Modal from '@/components/Modal'

import { LocalBackupStateEnum, PermissionKeysEnum } from '@/constants/enum'

const { Item } = Form
const { Search } = Input

interface CreateWalletButtonProps {
  total: number
}

const CreateWalletButton: FC<CreateWalletButtonProps> = ({ total }) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  return (
    <>
      <WalletButton
        text={t('wallet.home.addWallet')}
        icon={<AddWalletIcon />}
        onClick={() => setOpen(true)}
      />
      <CreateWalletDrawer open={open} onClose={() => setOpen(false)} />
    </>
  )
}

interface SearchFormProps {
  total: number
  query: SearchState
}

const SearchForm: FC<SearchFormProps> = ({ query, total }) => {
  const dispatch = useDispatch<AppDispatch>()
  const { t } = useTranslation()
  const canManageWallet = useCanManageWallet()
  const searchVal = useSelectWalletsSearchValue()
  const workspaceMember = useSelectWorkspaceUserInfo()
  // 判断是否在自然日内下载钱包数据
  const { isSendAddressRecoveryFile, setIsSendAddressRecoveryFile } =
    useGetIsSendAddressRecoveryFile(workspaceMember?.backupState)
  const onSearch = () => {
    dispatch(setWalletsQuery({ name: searchVal, pageNumber: 1 }))
  }
  const onSelectAccountType = (value: WalletType) => {
    dispatch(setWalletsQuery({ accountType: value }))
  }
  const onBack = () => {
    dispatch(resetWalletsQuery())
  }
  const options = [
    {
      label: t('wallet.home.allType'),
      value: '',
    },
    {
      label: t('wallet.home.assetsType'),
      value: WalletType.asset,
    },
    {
      label: t('wallet.home.web3Type'),
      value: WalletType.web3,
    },
  ]
  const [page, setPage] = useState(1)
  const [id, setId] = useState('')
  useEffect(() => {
    // 筛选出隐藏的钱包直接过滤
    setContent([...content.filter(item => item.id !== id)])
    // 总数减少一个，防止无限滚动判断出错
    setTotalEle(totalEle - 1)
  }, [id])

  // 无限滚动
  const [content, setContent] = useState<WalletItemModel[]>([] as WalletItemModel[])
  const [totalEle, setTotalEle] = useState(0)
  const [loading, setLoading] = useState(false)

  // 获取数据
  const loadMoreData = async (refresh?: boolean) => {
    // 无限滚动获取数据
    if (loading) {
      return
    }
    setLoading(true)
    // 每次打开页面重新获取数据
    if (refresh) {
      const res = await walletApi.getHideWalletList({
        pageNumber: 1,
        pageSize: DEFAULT_WALLET_LIST_PAGE_SIZE,
      })
      if (res.success) {
        setTotalEle(res?.data?.totalElements)
        setPage(2)
        setContent([...res?.data?.content])
      }
    } else {
      const res = await walletApi.getHideWalletList({
        pageNumber: page,
        pageSize: DEFAULT_WALLET_LIST_PAGE_SIZE,
      })
      if (res.success) {
        setPage(page + 1)
        setContent([...content, ...res?.data?.content])
      }
    }

    setLoading(false)
  }

  // 隐藏钱包内容
  const showWalletTitle = (
    <div className={styles.showWalletTitle}>
      {t('wallet.home.hideWalletDesc3')}
      <div style={{ color: '#888E8E', fontSize: '14px' }}>{t('wallet.home.showWalletTitle')}</div>
    </div>
  )

  const showWalletContent = (
    <div className={styles.showWallet} id="scrollableDiv">
      <div style={{ marginTop: '30px' }}>
        {content.length > 0 ? (
          <InfiniteScroll
            dataLength={content.length}
            next={loadMoreData}
            hasMore={content.length < totalEle}
            loader={
              <Row gutter={[14, 14]}>
                <Col span={12} xxl={12} className={styles.col}>
                  <Card style={{ width: '367px', marginTop: '10px' }}>
                    <Skeleton
                      active
                      paragraph={{ rows: 3, width: ['50%', '30%', '60%'] }}
                      title={false}
                      style={{ height: 112, marginTop: '10px' }}
                    />
                  </Card>
                </Col>
                <Col span={12} xxl={12} className={styles.col}>
                  <Card style={{ width: '367px', marginTop: '10px' }}>
                    <Skeleton
                      active
                      paragraph={{ rows: 3, width: ['50%', '30%', '60%'] }}
                      title={false}
                      style={{ height: 112, marginTop: '10px' }}
                    />
                  </Card>
                </Col>
              </Row>
            }
            scrollableTarget="scrollableDiv"
          >
            <div className={styles.cards}>
              <Row gutter={14}>
                {content?.map((wallet, index) => {
                  return (
                    <CardContent
                      wallet={wallet}
                      loading={loading}
                      index={index}
                      isHide={false}
                      key={index}
                      setId={setId}
                    />
                  )
                })}
              </Row>
            </div>
          </InfiniteScroll>
        ) : (
          <Empty></Empty>
        )}
      </div>
    </div>
  )
  const modalRef = useRef<any>(null)
  const [btnLoading, setBtnloading] = useState(false)
  // 展示出隐藏钱包
  const showWallet = async () => {
    setBtnloading(true)
    await loadMoreData(true)
    setBtnloading(false)
    modalRef.current = Modal.info({
      title: showWalletTitle,
      content: showWalletContent,
      footer: null,
      closable: true,
      icon: null,
      width: 800,
    })
  }
  useEffect(() => {
    modalRef.current?.update({
      title: showWalletTitle,
      content: showWalletContent,
      footer: null,
      closable: true,
      icon: null,
      width: 800,
      afterClose: () => {
        setPage(1)
        // 只有点击了显示钱包才会刷新显示的钱包
        if (id !== '') {
          dispatch(resetShowWallets())
        }
        setId('')
      },
    })
  }, [content])

  // 下载钱包数据
  const [fileLoading, setFileLoading] = useState(false)
  const handleOkDownload = async (close: any) => {
    setFileLoading(true)
    const res = await walletApi.sendAddressRecoveryFile()
    setFileLoading(false)
    if (res.data) {
      Modal.info({
        title: t('wallet.home.downloadTitle2'),
        content: <div style={{ marginTop: '40px' }}></div>,
        closable: true,
      })
      setIsSendAddressRecoveryFile(false)
    }
    close()
  }
  const handleDownload = () => {
    Modal.confirm({
      title: t('wallet.home.downloadTitle'),
      content: <div>{t('wallet.home.downloadDesc')}</div>,
      closable: true,
      onOk: close => {
        handleOkDownload(close)
      },
      okButtonProps: {
        loading: fileLoading,
      },
    })
  }
  // 判断是否有钱包管理权限
  const isWalletManage = useIsManagement(PermissionKeysEnum.MANAGEMENT_VAULT_ASSETS)

  return (
    <Form className={styles.form}>
      <div className={styles.items}>
        <Item className={styles.walletType}>
          <Select
            style={{ width: 170 }}
            options={options}
            value={query.accountType}
            onChange={onSelectAccountType}
          />
        </Item>
        <Item>
          <Search
            value={searchVal}
            onChange={evt => dispatch(setWalletsSearchValue(evt.target.value))}
            placeholder={t('wallet.home.searchPlaceholder') || ''}
            enterButton={
              <Button style={{ color: '#FFFFFF' }} type="primary">
                {t('common.search')}
              </Button>
            }
            onSearch={onSearch}
            size="small"
            style={{ width: 450 }}
          />
        </Item>
        {(query.name || query.accountType) && (
          <div className={styles.backBtn}>
            <Button type="link" onClick={onBack}>
              {t('wallet.home.backWalletList')}
            </Button>
          </div>
        )}
        {isWalletManage && (
          <div
            onClick={showWallet}
            className={cls([styles.showBtn, btnLoading && styles.disableClick])}
          >
            <img src={HideWallet2} alt="" style={{ width: '12px', height: '12px' }} />
            <span style={{ marginLeft: '2px', fontSize: '12px', color: '#424649' }}>
              {t('wallet.home.hideWalletDesc3')}
            </span>
          </div>
        )}
        {workspaceMember?.backupState === LocalBackupStateEnum.COMPLETE && (
          <div className={styles.backBtn} style={{ marginLeft: 'auto', marginRight: '10px' }}>
            <Button type="link" onClick={handleDownload} disabled={!isSendAddressRecoveryFile}>
              {t('wallet.home.downloadBtn')}
            </Button>
          </div>
        )}
      </div>
      {canManageWallet && <CreateWalletButton total={total} />}
    </Form>
  )
}
interface CardProps {
  loading: boolean
  wallet: WalletItemModel
  index: number
  isHide?: boolean
  navigate?: NavigateFunction
  setId?: Function
}

const CardContent: FC<CardProps> = ({ loading, wallet, index, isHide, navigate, setId }) => {
  const { accountName, usdBalance, accountKey, accountType = 1, addressList, id } = wallet
  const address = addressList?.[0]?.address ?? ''
  const isWeb3 = accountType === 2
  const { t } = useTranslation()

  const [unMaskAddrMap, setUnMaskAddrMap] = useState<Record<string, boolean | undefined>>({})
  const user = useSelectWorkspaceUserInfo()
  const isChecking = useRef<boolean>(false)
  const checkBackupStatus = useCheckBackupStatus()
  const onClickEye = async (address: string) => {
    if (isChecking.current) {
      return
    }
    isChecking.current = true
    const backUp = await checkBackupStatus()
    isChecking.current = false
    if (!backUp) {
      BackupModal.open(user?.role)
      return
    }
    setUnMaskAddrMap({
      ...unMaskAddrMap,
      [address]: !unMaskAddrMap[address],
    })
  }

  const ref = useRef(null)
  const isHovering = useHover(ref)
  const dispatch = useDispatch()
  // 判断是否有钱包管理权限
  const isWalletManage = useIsManagement(PermissionKeysEnum.MANAGEMENT_VAULT_ASSETS)
  const [hideLoading, setHideLoading] = useState(false)

  // 隐藏或显示钱包
  const handleIsHideWallet = async (hide: boolean) => {
    setHideLoading(true)
    const res = await walletApi.setAccountHiddenStatus({
      accountKey,
      hiddenStatus: hide ? 1 : 0,
    })
    if (res.success) {
      // 如果是显示钱包就传递id进行筛选
      if (!hide && setId) {
        setId(id)
        setHideLoading(false)
        return
      } else {
        dispatch(resetShowWallets())
        setHideLoading(false)
      }
    }
  }

  return (
    <Col
      span={isHide ? 8 : 12}
      xxl={isHide ? 6 : 12}
      key={accountKey || index}
      className={styles.col}
      ref={ref}
    >
      <Card
        className={cls([styles.card, isWeb3 && styles.web3])}
        hoverable={!loading}
        onClick={async () => {
          if (loading) {
            return
          }
          if (isWeb3 && isHide) {
            if (isChecking.current) {
              return
            }
            isChecking.current = true
            const backUp = await checkBackupStatus()
            isChecking.current = false
            if (!backUp) {
              BackupModal.open(user?.role)
              return
            }
            if (navigate) {
              navigate(`/wallet/${wallet.accountKey}_web3`)
            }

            return
          }
          if (navigate) {
            navigate(`/wallet/${wallet.accountKey}`)
          }
        }}
      >
        <Skeleton
          active
          loading={loading}
          paragraph={{ rows: 3, width: ['50%', '30%', '60%'] }}
          title={false}
          style={{ height: 112 }}
        >
          <h3 style={{ wordBreak: 'break-all' }}>
            {accountName}

            {isHovering && isWalletManage && isHide ? (
              <Tooltip placement="top" title={t('wallet.home.hideWallet')}>
                <img
                  src={HideWallet}
                  alt=""
                  className={styles.img}
                  style={{ marginLeft: '4px', marginBottom: '2px' }}
                  onClick={(evt: any) => {
                    evt.stopPropagation()
                    Modal.confirm({
                      title: t('wallet.home.hideWalletTitle', { walletName: accountName }),
                      content: (
                        <div>
                          <div>{t('wallet.home.hideWalletDesc')}</div>
                          <div>{t('wallet.home.hideWalletDesc2')}</div>
                        </div>
                      ),
                      okText: t('wallet.home.hideWalletBtn'),
                      closable: true,
                      onOk: () => handleIsHideWallet(true),
                      okButtonProps: {
                        loading: hideLoading,
                      },
                    })
                  }}
                />
              </Tooltip>
            ) : null}
            {!isHide && (
              <Tooltip placement="top" title={t('wallet.home.showWallet')}>
                <img
                  src={ShowWallet}
                  alt=""
                  className={styles.img}
                  style={{ marginLeft: '4px', marginBottom: '2px' }}
                  onClick={(evt: any) => {
                    evt.stopPropagation()
                    Modal.confirm({
                      title: t('wallet.home.showWalletTitle2', { walletName: accountName }),
                      content: (
                        <div>
                          <div>{t('wallet.home.showWalletDesc')}</div>
                        </div>
                      ),
                      okText: t('wallet.home.showWalletBtn'),
                      onOk: () => handleIsHideWallet(false),
                      closable: true,
                      okButtonProps: {
                        loading: hideLoading,
                      },
                    })
                  }}
                />
              </Tooltip>
            )}
          </h3>
          {isWeb3 ? (
            <>
              <span>{t('common.address')}</span>
              <div className={styles.info}>
                {unMaskAddrMap[address] ? (
                  <>
                    <div className={styles.fullAddress}>
                      {address}
                      <div
                        className={styles.copy}
                        onClick={(evt: any) => {
                          evt.stopPropagation()
                          const success = copyTextToClipboard(address)
                          if (success) {
                            window.shMessage.success(t('clipboard.copySuccess'))
                          }
                        }}
                      >
                        <CopyIcon />
                      </div>
                    </div>
                    <div
                      className={styles.eyeIcon}
                      onClick={evt => {
                        evt.stopPropagation()
                        onClickEye(address)
                      }}
                    >
                      <OpenEyeIcon />
                    </div>
                  </>
                ) : (
                  <>
                    {maskAddress(address)}
                    <div
                      className={styles.eyeIcon}
                      onClick={evt => {
                        evt.stopPropagation()
                        onClickEye(address)
                      }}
                    >
                      <CloseEyeIcon />
                    </div>
                  </>
                )}
              </div>
              <div className={styles.badge}>
                <div className={styles.badgeName}>{WALLET_TYPE()[accountType]}</div>
              </div>
            </>
          ) : (
            <>
              <span>{t('wallet.home.balance', { currency: CURRENCY })}</span>
              <div className={styles.info}>$ {formatUSD(usdBalance)}</div>
              <div className={styles.badge}>
                <div className={styles.badgeName}>{WALLET_TYPE()[accountType]}</div>
              </div>
            </>
          )}
        </Skeleton>
      </Card>
    </Col>
  )
}
interface CardsProps {
  loading: boolean
  data: WalletPaginationModel['content']
}

const Cards: FC<CardsProps> = ({ data, loading }) => {
  const dispatch = useDispatch<AppDispatch>()
  const { t } = useTranslation()
  const navigate = useNavigate()

  const content: WalletItemModel[] = loading
    ? new Array(DEFAULT_WALLET_LIST_PAGE_SIZE).fill({})
    : data
  if (content.length === 0) {
    const onBack = () => {
      dispatch(resetWalletsQuery())
    }
    return (
      <Empty>
        <Button type="primary" onClick={onBack}>
          {t('wallet.home.emptyBtn')}
        </Button>
      </Empty>
    )
  }

  return (
    <div className={styles.cards}>
      <Row gutter={14}>
        {content.map((wallet, index) => {
          return (
            <CardContent
              wallet={wallet}
              loading={loading}
              index={index}
              isHide={true}
              navigate={navigate}
              key={index}
            />
          )
        })}
      </Row>
    </div>
  )
}

interface WalletPaginationProps {
  total: number
  current: number
  onChange: PaginationProps['onChange']
}

const WalletPagination: FC<WalletPaginationProps> = ({ current, onChange, total }) => {
  const { t } = useTranslation()

  return (
    <div className={styles.pagination}>
      <Pagination
        current={current}
        onChange={onChange}
        total={total}
        showSizeChanger={false}
        showQuickJumper
        showTotal={total => t('common.total', { total })}
        pageSize={DEFAULT_WALLET_LIST_PAGE_SIZE}
      />
    </div>
  )
}

interface SearchState {
  accountType: WalletType
  name: string
}

const WalletList = () => {
  const location = useLocation()
  const { data, isLoading, query, setQuery } = useGetWalletList(
    location.search.slice(1).split('&')[0]
  )
  const total = data.totalElements
  const showPagination = Math.ceil(total / DEFAULT_WALLET_LIST_PAGE_SIZE) > 1
  return (
    <div className={styles.container} style={showPagination ? { paddingBottom: '74px' } : {}}>
      <SearchForm total={total} query={query} />
      <Cards data={data.content} loading={isLoading} />
      {showPagination && (
        <>
          <ShadowView style={{ bottom: 74 }} />
          <WalletPagination
            current={query.pageNumber}
            onChange={page => setQuery({ pageNumber: page })}
            total={total}
          />
        </>
      )}
    </div>
  )
}

export default React.memo(WalletList)
